
import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { EventFormValues } from '../../schemas/eventFormSchema';

interface DateTimeFieldsWatcherProps {
  form: UseFormReturn<EventFormValues>;
}

const DateTimeFieldsWatcher = ({ form }: DateTimeFieldsWatcherProps) => {
  // Watch the startDate to update endDate when it changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'startDate') {
        const startDate = value.startDate;
        
        // If this is a multi-day event, set the end date to startDate + 1 day
        const isMultiDay = form.getValues('timeNotSpecified') === false || 
                          (form.getValues('startDate')?.toDateString() !== form.getValues('endDate')?.toDateString());
        
        if (startDate && isMultiDay) {
          // Set the end date to the day after the start date
          const newEndDate = new Date(startDate);
          newEndDate.setDate(newEndDate.getDate() + 1);
          form.setValue('endDate', newEndDate, { shouldDirty: true });
        } else if (startDate) {
          // For single-day events, set end date equal to start date
          form.setValue('endDate', new Date(startDate), { shouldDirty: true });
        }
      }
      
      // When the time specification changes, set default start/end times
      if (name === 'timeNotSpecified') {
        const timeNotSpecified = value.timeNotSpecified;
        
        if (timeNotSpecified === false) {
          // Set start time to 9:00 AM
          const startTime = new Date();
          startTime.setHours(9, 0, 0, 0);
          form.setValue('startTime', startTime);
          
          // Set end time to 5:00 PM
          const endTime = new Date();
          endTime.setHours(17, 0, 0, 0);
          form.setValue('endTime', endTime);
        }
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form]);
  
  // Initialize default times when component first renders
  useEffect(() => {
    // Set default times if they haven't been set yet
    const startTime = form.getValues('startTime');
    const endTime = form.getValues('endTime');
    
    if (!startTime) {
      const defaultStartTime = new Date();
      defaultStartTime.setHours(9, 0, 0, 0);
      form.setValue('startTime', defaultStartTime);
    }
    
    if (!endTime) {
      const defaultEndTime = new Date();
      defaultEndTime.setHours(17, 0, 0, 0);
      form.setValue('endTime', defaultEndTime);
    }
  }, [form]);
  
  return null; // This is a non-visual component
};

export default DateTimeFieldsWatcher;
